import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/checkbox/checkbox.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, inputChecked, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import type { Product } from "../lib/types";

// Wire shapes of internal/modules/ingest (domain.go / routes.go). The Go
// structs marshal OrgID as "tenant" and nest provenance; time fields carry
// `omitzero`, so they may be absent entirely.
interface IngestSource {
  id: string;
  tenant: string;
  kind: string; // document | api | paste
  name?: string;
  digest: string;
  reference?: string;
  mediaType?: string;
  fetchedAt?: string;
  url?: string;
  createdBy?: string;
}

interface IngestProvenance {
  sourceId: string;
  extractor: string; // structured | labelled | ocr
  locator: string;
  raw?: string;
}

type ProposalStatus = "pending" | "accepted" | "rejected" | "superseded";

interface Proposal {
  id: string;
  tenant: string;
  targetKind: string; // product | batch
  targetId: string;
  field: string;
  value: string;
  confidence: number;
  provenance: IngestProvenance;
  status: ProposalStatus;
  current?: string;
  decidedBy?: string;
  decidedAt?: string;
  createdAt?: string;
}

interface IngestResult {
  source: IngestSource;
  proposals: Proposal[] | null;
  skipped?: string[] | null;
}

// What the last upload/fetch produced, kept on screen so "42 proposed,
// 3 skipped" survives the queue re-render.
interface Outcome {
  sourceName: string;
  proposed: number;
  skipped: string[];
}

const STATUS_LABELS: Record<ProposalStatus, string> = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
  superseded: "Superseded",
};

// The ingest review console. Extraction PROPOSES, a human DISPOSES: uploads
// and fetches only ever create proposals, and nothing reaches a passport
// except through an explicit accept on this page (the server's
// /proposals/decide is the only write path). Gated to editors/org admins —
// the server enforces the same via requireEditor.
@customElement("ingest-page")
export class IngestPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .intake {
        display: grid;
        gap: var(--space-4, 16px);
        grid-template-columns: 1fr;
      }

      @media (min-width: 840px) {
        .intake {
          grid-template-columns: 1fr 1fr;
        }
      }

      .intake .card {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .intake h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font: 500 16px/1.2 var(--font-plain, sans-serif);
      }

      .intake h2 md-icon {
        --md-icon-size: 20px;
        color: var(--md-sys-color-primary);
      }

      .intake p {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
      }

      .intake .grow {
        flex: 1;
      }

      .file-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }

      .file-name {
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
        word-break: break-all;
      }

      input[type="file"] {
        display: none;
      }

      .rule-note {
        display: flex;
        gap: 10px;
        align-items: flex-start;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        border-radius: 12px;
        padding: 12px 16px;
        font-size: 0.8125rem;
        line-height: 1.45;
      }

      .rule-note md-icon {
        --md-icon-size: 20px;
        flex-shrink: 0;
        margin-top: 1px;
      }

      .outcome {
        border-top: 1px solid var(--md-sys-color-outline-variant);
        padding-top: 12px;
        font-size: 0.8125rem;
      }

      .outcome ul {
        margin: 6px 0 0;
        padding-left: 18px;
        color: var(--md-sys-color-on-surface-variant);
      }

      .bulkbar {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        padding: 10px 20px;
        background: var(--md-sys-color-surface-container);
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
        font-size: 0.8125rem;
      }

      .bulkbar span {
        flex: 1;
        min-width: 120px;
      }

      .group-head td {
        background: var(--md-sys-color-surface-container-low);
        font: 500 13px/1.2 var(--font-plain, sans-serif);
        padding-top: 8px;
        padding-bottom: 8px;
      }

      td.check-cell,
      th.check-cell {
        width: 36px;
        padding-right: 0;
      }

      .row-actions {
        display: flex;
        gap: 4px;
        justify-content: flex-end;
        white-space: nowrap;
      }

      .confidence {
        font: 500 12px/1 var(--font-mono, monospace);
        color: var(--md-sys-color-on-surface-variant);
        white-space: nowrap;
      }

      .confidence--low {
        color: var(--md-sys-color-error);
      }

      details.history summary {
        cursor: pointer;
        padding: 14px 20px;
        font: 500 14px/1.2 var(--font-plain, sans-serif);
        list-style: none;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      details.history summary::-webkit-details-marker {
        display: none;
      }

      details.history summary md-icon {
        --md-icon-size: 20px;
        transition: transform 0.15s;
      }

      details.history[open] summary md-icon {
        transform: rotate(180deg);
      }

      details.history summary small {
        font-weight: 400;
        color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private products: Product[] = [];
  // GET /fields — key → labels recognised for it; labels[0] is the human
  // label the owning module defines (FieldWriter.Fields → vocabulary).
  @state() private fields: Record<string, string[]> = {};
  @state() private proposals: Proposal[] = [];
  @state() private loading = true;
  @state() private error = "";

  // Upload card.
  @state() private uploadTarget = "";
  @state() private uploadFileName = "";
  @state() private uploading = false;

  // URL card.
  @state() private url = "";
  @state() private urlTarget = "";
  @state() private fetching = false;

  @state() private outcome: Outcome | null = null;
  @state() private selected = new Set<string>();
  @state() private deciding = false;

  @query("#file-input") private fileInput?: HTMLInputElement;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  // The server's requireEditor accepts editor, org_admin and platform
  // operators; mirror it so we never show controls a request would refuse.
  private get canReview(): boolean {
    return (
      this.auth.hasRole("editor") ||
      this.auth.hasRole("org_admin") ||
      this.auth.hasRole("platform_operator")
    );
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      // Independent reads, issued together. status=all so the history section
      // needs no second round trip; pending vs decided is split client-side.
      const [fieldsResp, products, proposalsResp] = await Promise.all([
        api.get<{ fields: Record<string, string[]> | null }>("/ingest/fields"),
        api.get<Product[]>("/catalog/products"),
        api.get<{ proposals: Proposal[] | null }>(
          "/ingest/proposals?status=all",
        ),
      ]);
      this.fields = fieldsResp.fields ?? {};
      this.products = products;
      this.proposals = proposalsResp.proposals ?? [];
    } catch (err) {
      this.error = errorMessage(err, "Failed to load the ingest queue.");
    } finally {
      this.loading = false;
    }
  }

  private async reloadProposals(): Promise<void> {
    try {
      const resp = await api.get<{ proposals: Proposal[] | null }>(
        "/ingest/proposals?status=all",
      );
      this.proposals = resp.proposals ?? [];
    } catch {
      // Keep what we have; the next full load will reconcile.
    }
  }

  private get pending(): Proposal[] {
    return this.proposals.filter((p) => p.status === "pending");
  }

  private get decided(): Proposal[] {
    return this.proposals
      .filter((p) => p.status !== "pending")
      .sort((a, b) =>
        (b.decidedAt ?? b.createdAt ?? "").localeCompare(
          a.decidedAt ?? a.createdAt ?? "",
        ),
      );
  }

  // The queue grouped by target, so a reviewer works product by product.
  // Server order (least confident first) is preserved within each group.
  private get pendingByTarget(): Array<{ targetId: string; rows: Proposal[] }> {
    const groups = new Map<string, Proposal[]>();
    for (const p of this.pending) {
      const rows = groups.get(p.targetId);
      if (rows) rows.push(p);
      else groups.set(p.targetId, [p]);
    }
    return [...groups.entries()].map(([targetId, rows]) => ({
      targetId,
      rows,
    }));
  }

  private fieldLabel(key: string): string {
    return this.fields[key]?.[0] ?? key;
  }

  private productName(targetId: string): string {
    return this.products.find((p) => p.ID === targetId)?.Name ?? targetId;
  }

  private sourceLabel(p: Proposal): string {
    const via =
      p.provenance.extractor === "ocr"
        ? " (OCR)"
        : p.provenance.extractor === "labelled"
          ? " (label match)"
          : "";
    return `${p.provenance.locator}${via}`;
  }

  // ---- intake -------------------------------------------------------------

  private onFilePicked(): void {
    this.uploadFileName = this.fileInput?.files?.[0]?.name ?? "";
  }

  private showOutcome(sourceName: string, res: IngestResult): void {
    const proposed = res.proposals?.length ?? 0;
    const skipped = res.skipped ?? [];
    this.outcome = { sourceName, proposed, skipped };
    if (proposed === 0 && skipped.length === 0) {
      toast(
        "Nothing new to propose — every value matched what is already there.",
      );
    } else {
      toast(
        `${proposed} value${proposed === 1 ? "" : "s"} proposed for review` +
          (skipped.length ? `, ${skipped.length} skipped` : ""),
      );
    }
  }

  private async handleUpload(): Promise<void> {
    const file = this.fileInput?.files?.[0];
    if (!file) {
      toast("Choose a file first.", true);
      return;
    }
    this.uploading = true;
    try {
      // routes.go POST /document reads multipart fields "file", "targetKind",
      // "targetId" and optional "reference". api.post passes FormData through
      // untouched (no Content-Type header), so the browser sets the boundary.
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("targetKind", "product");
      if (this.uploadTarget) form.append("targetId", this.uploadTarget);
      const res = await api.post<IngestResult>("/ingest/document", form);
      this.showOutcome(file.name, res);
      if (this.fileInput) this.fileInput.value = "";
      this.uploadFileName = "";
      await this.reloadProposals();
    } catch (err) {
      toast(errorMessage(err, "The document could not be read."), true);
    } finally {
      this.uploading = false;
    }
  }

  private async handleFetch(): Promise<void> {
    const url = this.url.trim();
    if (!url) {
      toast("Enter a URL first.", true);
      return;
    }
    this.fetching = true;
    try {
      const res = await api.post<IngestResult>("/ingest/url", {
        url,
        targetKind: "product",
        targetId: this.urlTarget,
      });
      this.showOutcome(url, res);
      this.url = "";
      await this.reloadProposals();
    } catch (err) {
      toast(errorMessage(err, "The URL could not be fetched."), true);
    } finally {
      this.fetching = false;
    }
  }

  // ---- review -------------------------------------------------------------

  private toggleSelected(id: string, checked: boolean): void {
    const next = new Set(this.selected);
    if (checked) next.add(id);
    else next.delete(id);
    this.selected = next;
  }

  private toggleGroup(rows: Proposal[], checked: boolean): void {
    const next = new Set(this.selected);
    for (const p of rows) {
      if (checked) next.add(p.id);
      else next.delete(p.id);
    }
    this.selected = next;
  }

  private async decide(ids: string[], accept: boolean): Promise<void> {
    if (ids.length === 0) return;
    this.deciding = true;
    try {
      const resp = await api.post<{ decided: Proposal[] | null }>(
        "/ingest/proposals/decide",
        { ids, accept },
      );
      const n = resp.decided?.length ?? ids.length;
      toast(
        accept
          ? `${n} value${n === 1 ? "" : "s"} accepted and applied.`
          : `${n} proposal${n === 1 ? "" : "s"} rejected.`,
      );
    } catch (err) {
      // On a partial failure the server reports what DID apply alongside the
      // error; the reload below shows the true state either way.
      toast(errorMessage(err, "The decision could not be applied."), true);
    } finally {
      const remaining = new Set(this.selected);
      for (const id of ids) remaining.delete(id);
      this.selected = remaining;
      await this.reloadProposals();
      this.deciding = false;
    }
  }

  // ---- render -------------------------------------------------------------

  render() {
    return html`
      <app-shell active="/ingest">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Data intake</h1>
              <p>
                Upload the paperwork you already have — certificates, lab
                reports, spreadsheets, ERP exports — and review what was read.
              </p>
            </div>
          </div>

          <div class="rule-note">
            ${icon("verified_user")}
            <span>
              <b>Extraction proposes, you dispose.</b> Nothing extracted here is
              ever written to a passport on its own. Every value arrives as a
              proposal with its source attached, and reaches a product only when
              you accept it — because a confidently wrong claim under your name
              is worse than a blank field.
            </span>
          </div>

          <div class="rule-note">
            ${icon("edit_note")}
            <span>
              <b>Typing values in yourself?</b> Every passport field can also be
              filled by hand: open the product from
              <a href="/products">Products</a> — its Passport tab is a manual
              editor. This page just saves the typing when a document already
              carries the data.
            </span>
          </div>

          ${
            this.loading
              ? html`<div class="center-loading">
                  <md-circular-progress indeterminate></md-circular-progress>
                </div>`
              : this.error
                ? html`<p class="error-text">${this.error}</p>`
                : !this.canReview
                  ? html`<div class="card empty">
                      Ingest is available to editors and organization admins.
                      Ask an admin for the editor role to upload documents and
                      review proposals.
                    </div>`
                  : html`
                      ${this.renderIntake()} ${this.renderQueue()}
                      ${this.renderHistory()}
                    `
          }
        </div>
      </app-shell>
    `;
  }

  private renderTargetSelect(
    label: string,
    value: string,
    onChange: (v: string) => void,
  ) {
    return html`
      <md-outlined-select
        label=${label}
        .value=${value}
        @change=${(e: Event) => onChange(inputValue(e))}
      >
        <md-select-option value="">
          <div slot="headline">No default — rows name their product</div>
        </md-select-option>
        ${this.products.map(
          (p) => html`
            <md-select-option value=${p.ID}>
              <div slot="headline">${p.Name}${p.SKU ? ` (${p.SKU})` : ""}</div>
            </md-select-option>
          `,
        )}
      </md-outlined-select>
    `;
  }

  private renderIntake() {
    return html`
      <div class="intake">
        <div class="card">
          <h2>${icon("description")} Upload a document</h2>
          <p>
            CSV, JSON, XML, text or PDF. A spreadsheet column naming the product
            lets one file fill many products; otherwise pick a target below.
          </p>
          <div class="file-row">
            <md-outlined-button
              ?disabled=${this.uploading}
              @click=${() => this.fileInput?.click()}
            >
              <span slot="icon">${icon("attach_file")}</span>
              Choose file
            </md-outlined-button>
            <span class="file-name">
              ${this.uploadFileName || "No file chosen"}
            </span>
          </div>
          <input
            id="file-input"
            type="file"
            @change=${() => this.onFilePicked()}
          />
          ${this.renderTargetSelect(
            "Target product",
            this.uploadTarget,
            (v) => (this.uploadTarget = v),
          )}
          <div class="grow"></div>
          <div class="actions-row">
            <md-filled-button
              ?disabled=${this.uploading || !this.uploadFileName}
              @click=${() => void this.handleUpload()}
            >
              ${this.uploading ? "Reading…" : "Extract values"}
            </md-filled-button>
          </div>
        </div>

        <div class="card">
          <h2>${icon("link")} Fetch from a URL</h2>
          <p>
            Pull a supplier or ERP endpoint that answers with JSON or CSV. The
            response is read the same way an upload is.
          </p>
          <md-outlined-text-field
            label="URL"
            type="url"
            placeholder="https://erp.example.com/api/products/export"
            .value=${this.url}
            @input=${(e: Event) => (this.url = inputValue(e))}
          ></md-outlined-text-field>
          ${this.renderTargetSelect(
            "Target product (optional)",
            this.urlTarget,
            (v) => (this.urlTarget = v),
          )}
          <div class="grow"></div>
          <div class="actions-row">
            <md-filled-button
              ?disabled=${this.fetching || !this.url.trim()}
              @click=${() => void this.handleFetch()}
            >
              ${this.fetching ? "Fetching…" : "Fetch and extract"}
            </md-filled-button>
          </div>
        </div>
      </div>
      ${this.outcome ? this.renderOutcome(this.outcome) : null}
    `;
  }

  private renderOutcome(o: Outcome) {
    return html`
      <div class="card outcome">
        <b>${o.sourceName}</b> — ${o.proposed}
        value${o.proposed === 1 ? "" : "s"}
        proposed${
          o.skipped.length
            ? html`, ${o.skipped.length} skipped:
                <ul>
                  ${o.skipped.map((s) => html`<li>${s}</li>`)}
                </ul>`
            : o.proposed === 0
              ? html`. Nothing was recognised — check the field vocabulary below
                or the file's column headers.`
              : "."
        }
      </div>
    `;
  }

  private confidenceBadge(c: number) {
    const pct = Math.round(c * 100);
    return html`<span class="confidence ${c < 0.6 ? "confidence--low" : ""}"
      >${pct}%</span
    >`;
  }

  private renderQueue() {
    const groups = this.pendingByTarget;
    if (groups.length === 0) {
      return html`<div class="card empty">
        Nothing waiting for review. Upload a document or fetch a URL to propose
        values.
      </div>`;
    }
    const selectedCount = this.selected.size;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>
            Awaiting your review
            <small
              >· ${this.pending.length}
              proposal${this.pending.length === 1 ? "" : "s"}, least confident
              first</small
            >
          </h2>
        </div>
        ${
          selectedCount > 0
            ? html`
                <div class="bulkbar">
                  <span>${selectedCount} selected</span>
                  <md-filled-button
                    ?disabled=${this.deciding}
                    @click=${() => void this.decide([...this.selected], true)}
                  >
                    <span slot="icon">${icon("check_circle")}</span>
                    Accept selected
                  </md-filled-button>
                  <md-text-button
                    ?disabled=${this.deciding}
                    @click=${() => void this.decide([...this.selected], false)}
                  >
                    Reject selected
                  </md-text-button>
                </div>
              `
            : null
        }
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th class="check-cell"></th>
                <th>Field</th>
                <th>Proposed value</th>
                <th>Confidence</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${groups.map((g) => this.renderGroup(g.targetId, g.rows))}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  private renderGroup(targetId: string, rows: Proposal[]) {
    const allSelected = rows.every((p) => this.selected.has(p.id));
    return html`
      <tr class="group-head">
        <td class="check-cell">
          <md-checkbox
            aria-label="Select all for ${this.productName(targetId)}"
            .checked=${allSelected}
            @change=${(e: Event) => this.toggleGroup(rows, inputChecked(e))}
          ></md-checkbox>
        </td>
        <td colspan="5">
          ${this.productName(targetId)}
          <small
            >${rows[0].targetKind} · ${rows.length}
            proposal${rows.length === 1 ? "" : "s"}</small
          >
        </td>
      </tr>
      ${rows.map(
        (p) => html`
          <tr>
            <td class="check-cell">
              <md-checkbox
                aria-label="Select ${this.fieldLabel(p.field)}"
                .checked=${this.selected.has(p.id)}
                @change=${(e: Event) =>
                  this.toggleSelected(p.id, inputChecked(e))}
              ></md-checkbox>
            </td>
            <td>
              <b>${this.fieldLabel(p.field)}</b>
              <small>${p.field}</small>
            </td>
            <td>
              ${p.value}
              ${
                p.current
                  ? html`<small>replaces: ${p.current}</small>`
                  : html`<small>currently empty</small>`
              }
            </td>
            <td>${this.confidenceBadge(p.confidence)}</td>
            <td>
              <code>${this.sourceLabel(p)}</code>
              ${
                p.provenance.raw && p.provenance.raw !== p.value
                  ? html`<small>read as: ${p.provenance.raw}</small>`
                  : null
              }
            </td>
            <td>
              <div class="row-actions">
                <md-text-button
                  ?disabled=${this.deciding}
                  @click=${() => void this.decide([p.id], true)}
                >
                  Accept
                </md-text-button>
                <md-text-button
                  ?disabled=${this.deciding}
                  @click=${() => void this.decide([p.id], false)}
                >
                  Reject
                </md-text-button>
              </div>
            </td>
          </tr>
        `,
      )}
    `;
  }

  private statusBadge(status: ProposalStatus) {
    switch (status) {
      case "accepted":
        return html`<span class="badge badge--ok"
          >${icon("verified")} ${STATUS_LABELS.accepted}</span
        >`;
      case "rejected":
        return html`<span class="badge badge--bad"
          >${icon("flag")} ${STATUS_LABELS.rejected}</span
        >`;
      default:
        return html`<span class="badge badge--neutral"
          >${icon("schedule")} ${STATUS_LABELS[status]}</span
        >`;
    }
  }

  private formatDate(value?: string): string {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  private renderHistory() {
    const decided = this.decided;
    if (decided.length === 0) return null;
    return html`
      <details class="history card card--flush">
        <summary>
          ${icon("expand_more")} History
          <small
            >· ${decided.length} decided
            proposal${decided.length === 1 ? "" : "s"}</small
          >
        </summary>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Field</th>
                <th>Value</th>
                <th>Outcome</th>
                <th>Decided</th>
              </tr>
            </thead>
            <tbody>
              ${decided.map(
                (p) => html`
                  <tr>
                    <td>${this.productName(p.targetId)}</td>
                    <td>${this.fieldLabel(p.field)}</td>
                    <td>${p.value}</td>
                    <td>${this.statusBadge(p.status)}</td>
                    <td>${this.formatDate(p.decidedAt)}</td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>
      </details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ingest-page": IngestPage;
  }
}
